import { HelpCircle, Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { UserMenu } from './UserMenu';

// Onsite Support (Zendesk) help article for Oscar. Opens in a new tab.
const HELP_URL = 'https://onsitesupport.zendesk.com/hc/en-au/articles/16489598039567-Oscar';

/**
 * Header — navy brand bar with orange bottom border, matching the Insights
 * report template (/insights/site/reports/Resources/report-template.html).
 *
 * Structure (left → right, per template):
 *   [ ☰ on mobile ] [ Onsite logo ] | [ App title (Oscar) ]
 *                                                          [ avatar ]
 *
 * The logo is served from /Logos/Onsite_Logo_RGB_white.svg — same asset
 * pattern as cab/insights/manage. Falls back to text if the image 404s.
 *
 * Clicking the logo (or the "Oscar" title) navigates back to the Dashboard
 * via a client-side route change. We deliberately do NOT hard-code an
 * absolute URL so the same header works in dev / staging / production
 * without an environment switch.
 */
export function Header({
  onMenuClick,
  navOpen,
}: {
  onMenuClick?: () => void;
  navOpen?: boolean;
}) {
  return (
    <header className="bg-navy text-white border-b-[3px] border-orange flex items-center justify-between px-3 sm:px-6 h-[54px] sticky top-0 z-50 gap-2">
      <div className="flex items-center gap-2 sm:gap-[14px] min-w-0">
        {onMenuClick && (
          <button
            type="button"
            onClick={onMenuClick}
            aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={navOpen ?? false}
            className="md:hidden inline-flex items-center justify-center h-9 w-9 -ml-1 rounded text-white/90 hover:bg-white/10"
          >
            {navOpen ? (
              <X className="h-5 w-5" aria-hidden />
            ) : (
              <Menu className="h-5 w-5" aria-hidden />
            )}
          </button>
        )}
        <Link
          to="/"
          aria-label="Oscar home"
          className="flex items-center gap-2 sm:gap-[14px] min-w-0 rounded hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <img
            src="/Logos/Onsite_Logo_RGB_white.svg"
            alt="Onsite Rental Group"
            className="h-6 sm:h-7 w-auto block shrink-0"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <div className="w-px h-[22px] bg-white/25 hidden sm:block" aria-hidden />
          <span className="font-heading text-[12px] sm:text-[13px] text-white/80 truncate">
            Oscar <span className="text-white/55">— IT asset &amp; user management</span>
          </span>
        </Link>
      </div>
      <div className="flex items-center gap-1 sm:gap-2 shrink-0">
        {/* Help (Onsite Support / Zendesk) — moved here from the sidebar so it
            sits alongside the avatar. Opens the help article in a new tab. */}
        <a
          href={HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Help (opens in a new tab)"
          title="Help"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <HelpCircle className="h-5 w-5" aria-hidden />
        </a>
        <UserMenu />
      </div>
    </header>
  );
}
